/**
 * Q1–7 scoring: sum `eCEAnswerResultWeight` per `eCEAnswerResult` label; tie-break by CMS result order.
 */
import type { QuizConfig, QuizQuestion } from '../model/types';

export interface ScoreMap {
  byLabel: Record<string, number>;
}

/**
 * Aggregate weights for Q1–7 only. Each question contributes the weight of the chosen answer
 * toward that answer's result label (eCEAnswerResult).
 *
 * @returns Map of result display name → summed weight (only labels that received points).
 */
export function scoreSelections(
  questions: readonly QuizQuestion[],
  selectedAnswerIndexPerQuestion: readonly (number | null)[],
): ScoreMap {
  const byLabel: Record<string, number> = {};
  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];
    const idx = selectedAnswerIndexPerQuestion[i];
    if (idx === null || idx === undefined) continue;
    const ans = q.answers[idx];
    if (!ans) continue;
    const k = ans.resultLabel;
    byLabel[k] = (byLabel[k] ?? 0) + ans.weight;
  }
  return { byLabel };
}

/**
 * Tie-breaking (documented):
 * 1. Highest total weight wins (sum of eCEAnswerResultWeight across Q1–7).
 * 2. If tied, the archetype that appears earlier in the CMS `quizResults` block list wins.
 *
 * @returns `null` when no weight was accumulated (no valid selections).
 */
export function winningResultFromScores(config: QuizConfig, scores: ScoreMap): { name: string; description: string } | null {
  const total = Object.values(scores.byLabel).reduce((a, b) => a + b, 0);
  if (total <= 0) return null;

  let bestScore = 0;
  for (const r of config.results) {
    bestScore = Math.max(bestScore, scores.byLabel[r.name] ?? 0);
  }

  for (const r of config.results) {
    if ((scores.byLabel[r.name] ?? 0) === bestScore) {
      return { name: r.name, description: r.description };
    }
  }
  return null;
}
